/**
 * @fileoverview Custom hook for checking if a template has arbitration documents.
 */

import {useQuery} from "react-query";
import {http as httpApiGateway} from "../../services/api/restApiClient";

type TemplateMetadataResponse = {
  hasArbitrationDocument: boolean;
};

/**
 * Custom hook for checking if a template has arbitration documents.
 * Calls the template_api Lambda via API Gateway.
 *
 * @param {string} facilityId The facility ID.
 * @param {string} templateName The template name.
 * @param {Object} options Query options.
 * @param {boolean} options.enabled Whether the query should be enabled.
 * @returns {Object} React Query result containing hasArbitrationDocument boolean.
 */
export const useTemplateHasArbitrationDocumentQuery = (
  facilityId: string | undefined,
  templateName: string | undefined,
  {enabled = true}: {enabled?: boolean} = {},
) =>
  useQuery<{hasArbitrationDocument: boolean}>(
    ["templateHasArbitrationDocument", facilityId, templateName],
    async () => {
      try {
        const metadataResponse = await httpApiGateway.get<TemplateMetadataResponse>(
          `/templates/facility/${facilityId}/${templateName}/metadata`,
        );

        if (metadataResponse.ok && metadataResponse.data) {
          return {
            hasArbitrationDocument:
              metadataResponse.data.hasArbitrationDocument ?? false,
          };
        }

        return {hasArbitrationDocument: false};
      } catch (error: any) {
        return {hasArbitrationDocument: false};
      }
    },
    {
      enabled: Boolean(enabled && facilityId && templateName),
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  );
